import { ApiResult } from "@/types/api";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  TourActCarouselResponse,
  TourActDetailItem,
  TourListingItem,
  TourListingRes,
} from "@/components/main-services/tour-act/tour.type";
import {
  getTourActDetailAction,
  GetTourAndActivityListingParams,
  getTourCarousel,
  getTourFilterCriteriaAction,
  handleGetTourAndActivityListing,
  handleSearchTourActAction,
  SearchTourActParams,
  TourFilterCriteriaResponse,
} from "@/actions/tour.action";

const tourQueryKeys = {
  carousel: () => ["tour-act-carousel"] as const,
  detail: (activityId: string, params: { start: string }) =>
    ["tour-act-detail", activityId, params] as const,
  listing: (params: GetTourAndActivityListingParams) =>
    ["tour-act-listings", params] as const,
  search: (params: SearchTourActParams) => ["tour-act-search", params] as const,
  criteria: () => ["tour-act-filter-criteria"] as const,
};

/** ******************************
 * Tour Carousel Hook
 ****************************** */
const useTourActCarousel = (enabled = true) => {
  return useQuery<
    ApiResult<TourActCarouselResponse>,
    Error,
    TourActCarouselResponse
  >({
    queryKey: tourQueryKeys.carousel(),
    queryFn: () => getTourCarousel(),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    select: (data) => data.data,
  });
};

/** ******************************
 * Tour Detail Hook
 ****************************** */
const useTourActDetail = (
  activityId: string,
  params: { start: string },
): UseQueryResult<TourActDetailItem, Error> => {
  return useQuery({
    queryKey: tourQueryKeys.detail(activityId, params),
    queryFn: () => getTourActDetailAction(activityId, params),
    enabled: Boolean(activityId),
    staleTime: 5 * 60 * 1000,
    select: (res: ApiResult<TourActDetailItem>) => res.data,
  });
};

/** ******************************
 * Tour Listing Hook
 ****************************** */
const useGetTourAndActivityListing = (
  params: GetTourAndActivityListingParams,
  enabled = true,
): UseQueryResult<TourListingItem[], Error> => {
  return useQuery({
    queryKey: tourQueryKeys.listing(params),
    queryFn: () => handleGetTourAndActivityListing(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
    select: (res: ApiResult<TourListingRes>) => res.data.data,
  });
};

/** ******************************
 * Tour & Activity Search Hook
 ****************************** */
const useTourAndActivitySearch = (
  params: SearchTourActParams,
  enabled = true,
): UseQueryResult<TourListingItem[], Error> => {
  return useQuery({
    queryKey: tourQueryKeys.search(params),
    queryFn: () => handleSearchTourActAction(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
    placeholderData: keepPreviousData,
    select: (res: ApiResult<TourListingRes>) => {
      if (!res?.data) return [];
      if (Array.isArray(res.data)) return res.data;
      return res.data.data ?? [];
    },
  });
};

/** ******************************
 * Tour Filter Criteria Hook
 ****************************** */
const useTourFilterCriteria = (enabled = true) => {
  return useQuery<TourFilterCriteriaResponse>({
    queryKey: tourQueryKeys.criteria(),
    queryFn: () => getTourFilterCriteriaAction(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

/** ******************************
 * Tour Search Mutation Hook
 * ****************************** */
const useSearchTourMutation = () => {
  return useMutation<ApiResult<TourListingRes>, Error, SearchTourActParams>({
    mutationFn: (params) => handleSearchTourActAction(params),
  });
};

export {
  tourQueryKeys,
  useTourActCarousel,
  useTourActDetail,
  useGetTourAndActivityListing,
  useTourAndActivitySearch,
  useSearchTourMutation,
  useTourFilterCriteria,
};
